import { useEffect, useRef, useState } from 'react';
import { useHandoffListQuery } from './useHandoff';
import { getNotificationsEnabled } from './useNotificationPreference';

const NOTIFY_TITLE = 'Nytt handoff-ärende';

function stripBadge(title: string): string {
  return title.replace(/^\(\d+\)\s*/, '');
}

function playBeep(): void {
  try {
    const Ctx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sine';
    osc.frequency.value = 880;
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.06, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.25);
    osc.start();
    osc.stop(ctx.currentTime + 0.26);
    osc.onended = () => {
      void ctx.close().catch(() => {});
    };
  } catch {}
}

export function useHandoffNotifications(enabled = true): void {
  const { data: list } = useHandoffListQuery({ enabled });
  const prevRequestedRef = useRef<Set<string> | null>(null);
  const unreadRef = useRef(0);

  const isLeaderRef = useRef(false);
  useEffect(() => {
    const locks = (navigator as Navigator & { locks?: LockManager }).locks;
    if (!locks) {
      isLeaderRef.current = true;
      return;
    }
    let release: (() => void) | null = null;
    const controller = new AbortController();
    locks
      .request(
        'handoff-notify-leader',
        { mode: 'exclusive', signal: controller.signal },
        () =>
          new Promise<void>((resolve) => {
            isLeaderRef.current = true;
            release = resolve;
            console.info('[handoff-notify] this tab is the notification leader');
          }),
      )
      .catch(() => {});
    return () => {
      isLeaderRef.current = false;
      controller.abort();
      release?.();
    };
  }, []);

  useEffect(() => {
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      void Notification.requestPermission().catch(() => {});
    }
  }, []);

  useEffect(() => {
    const clear = () => {
      unreadRef.current = 0;
      document.title = stripBadge(document.title);
    };
    const onVisibility = () => {
      if (!document.hidden) clear();
    };
    window.addEventListener('focus', clear);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', clear);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  useEffect(() => {
    if (!Array.isArray(list)) return;

    const currentRequested = new Set(
      list.filter((c) => c.handoffStatus === 'requested').map((c) => c.id),
    );

    if (prevRequestedRef.current === null) {
      prevRequestedRef.current = currentRequested;
      console.info('[handoff-notify] seeded queue', { requested: currentRequested.size });
      return;
    }

    const fresh = list.filter(
      (c) =>
        c.handoffStatus === 'requested' && !prevRequestedRef.current!.has(c.id) && !c.isMailHandoff,
    );
    prevRequestedRef.current = currentRequested;

    if (fresh.length === 0) return;

    if (!getNotificationsEnabled()) {
      console.info('[handoff-notify] suppressed — notifications toggled off', {
        count: fresh.length,
      });
      return;
    }

    const canNotify = typeof Notification !== 'undefined' && Notification.permission === 'granted';
    console.info('[handoff-notify] new handoff detected', {
      count: fresh.length,
      ids: fresh.map((c) => c.id),
      canNotify,
      permission: typeof Notification !== 'undefined' ? Notification.permission : 'unsupported',
      focused: document.hasFocus(),
    });

    if (!isLeaderRef.current) {
      console.info('[handoff-notify] not leader tab — skipping popup/sound');
    } else {
      if (canNotify) {
        const first = fresh[0];
        const body =
          fresh.length === 1
            ? `${first.customerName || 'En kund'} vill prata med en medarbetare`
            : `${fresh.length} nya ärenden väntar`;
        try {
          const notification = new Notification(NOTIFY_TITLE, {
            body,
            tag: `handoff-${first.id}`,
            requireInteraction: true,
          });
          notification.onclick = () => {
            window.focus();
            window.location.href = '/handoff';
            notification.close();
          };
        } catch (err) {
          console.warn('[handoff-notify] Notification construction failed', err);
        }
      }

      playBeep();
    }

    if (!document.hasFocus()) {
      unreadRef.current += fresh.length;
      document.title = `(${unreadRef.current}) ${stripBadge(document.title)}`;
    }
  }, [list]);
}

export function useNotificationPermission() {
  const [permission, setPermission] = useState<NotificationPermission | 'unsupported'>(
    typeof Notification === 'undefined' ? 'unsupported' : Notification.permission,
  );

  const request = async (): Promise<NotificationPermission> => {
    if (typeof Notification === 'undefined') return 'denied';
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      return result;
    } catch {
      return 'denied';
    }
  };

  const test = () => {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    try {
      new Notification('Testnotis', {
        body: 'Notiser fungerar! Du får en sådan här när ett nytt handoff-ärende kommer in.',
        requireInteraction: true,
      });
    } catch {}
  };

  return { permission, request, test };
}
